
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Trash2, Loader2, MessageSquare } from 'lucide-react';
import ImageUpload from './ImageUpload';

interface TestimonialItem {
    id: string;
    name: string;
    position: string;
    company: string;
    content: string;
    avatar: string;
    avatarAlt: string;
    rating: number;
}

export default function TestimonialsManager() {
    const [testimonials, setTestimonials] = useState<TestimonialItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);

    const [newItem, setNewItem] = useState<Partial<TestimonialItem>>({
        name: '',
        position: '',
        company: '',
        content: '',
        avatar: '',
        rating: 5
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const { data, error } = await supabase
                .from('site_content')
                .select('content')
                .eq('section_key', 'testimonials')
                .maybeSingle();

            if (error) throw error;
            if (data && data.content) {
                setTestimonials(data.content);
            }
        } catch (err) {
            console.error('Error fetching testimonials:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (updatedList: TestimonialItem[]) => {
        setSaving(true);
        try {
            const { error } = await supabase
                .from('site_content')
                .upsert({
                    section_key: 'testimonials',
                    content: updatedList,
                    updated_at: new Date().toISOString()
                });

            if (error) throw error;
            setTestimonials(updatedList);
            setIsFormOpen(false);
            setNewItem({ name: '', position: '', company: '', content: '', avatar: '', rating: 5 });
        } catch (err) {
            console.error('Error saving testimonials:', err);
            alert('Failed to save testimonials');
        } finally {
            setSaving(false);
        }
    };

    const addItem = () => {
        if (!newItem.name || !newItem.content) return;

        const itemToAdd: TestimonialItem = {
            id: Math.random().toString(36).substr(2, 9),
            name: newItem.name!,
            position: newItem.position || '',
            company: newItem.company || '',
            content: newItem.content!,
            avatar: newItem.avatar || '',
            avatarAlt: `${newItem.name}`,
            rating: newItem.rating || 5
        };

        const updated = [...testimonials, itemToAdd];
        handleSave(updated);
    };

    const deleteItem = (id: string) => {
        if (!confirm('Delete this testimonial?')) return;
        const updated = testimonials.filter(i => i.id !== id);
        handleSave(updated);
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-blue-500" /></div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-white">Testimonials</h2>
                <button
                    onClick={() => setIsFormOpen(!isFormOpen)}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                    {isFormOpen ? 'Cancel' : <><Plus size={18} /> Add Testimonial</>}
                </button>
            </div>

            {isFormOpen && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4 animate-in fade-in slide-in-from-top-4 duration-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Name</label>
                            <input
                                type="text"
                                value={newItem.name}
                                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Position</label>
                            <input
                                type="text"
                                value={newItem.position}
                                onChange={(e) => setNewItem({ ...newItem, position: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Company</label>
                            <input
                                type="text"
                                value={newItem.company}
                                onChange={(e) => setNewItem({ ...newItem, company: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Rating (1-5)</label>
                            <input
                                type="number"
                                min="1"
                                max="5"
                                value={newItem.rating}
                                onChange={(e) => setNewItem({ ...newItem, rating: parseInt(e.target.value) })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Testimonial Content</label>
                        <textarea
                            rows={3}
                            value={newItem.content}
                            onChange={(e) => setNewItem({ ...newItem, content: e.target.value })}
                            className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Avatar</label>
                        <div className="max-w-xs">
                            <ImageUpload
                                value={newItem.avatar || null}
                                onChange={(url) => setNewItem({ ...newItem, avatar: url })}
                                folder="avatars"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        <button
                            onClick={addItem}
                            disabled={saving || !newItem.name}
                            className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="animate-spin h-5 w-5" /> : 'Save Testimonial'}
                        </button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {testimonials.map((item) => (
                    <div key={item.id} className="bg-gray-900 border border-gray-800 rounded-xl p-6 flex flex-col gap-4 group hover:border-gray-700 transition-colors relative">
                        <button
                            onClick={() => deleteItem(item.id)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-2"
                        >
                            <Trash2 size={18} />
                        </button>

                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0">
                                {item.avatar ? (
                                    <img src={item.avatar} alt={item.name} className="h-12 w-12 rounded-full object-cover" />
                                ) : (
                                    <div className="h-12 w-12 rounded-full bg-gray-800 flex items-center justify-center text-gray-500">
                                        <MessageSquare size={20} />
                                    </div>
                                )}
                            </div>
                            <div>
                                <h3 className="font-semibold text-white">{item.name}</h3>
                                <p className="text-sm text-gray-400">{item.position} at {item.company}</p>
                            </div>
                        </div>

                        <p className="text-gray-300 text-sm italic">"{item.content}"</p>

                        <div className="flex text-yellow-500 text-xs">
                            {[...Array(5)].map((_, i) => (
                                <span key={i} className={i < item.rating ? 'opacity-100' : 'opacity-30'}>★</span>
                            ))}
                        </div>
                    </div>
                ))}

                {testimonials.length === 0 && !loading && (
                    <div className="col-span-full text-center py-12 text-gray-500 border-2 border-dashed border-gray-800 rounded-xl">
                        No testimonials added yet.
                    </div>
                )}
            </div>
        </div>
    );
}
